/**
 * Badge configuration constants shared between web and mobile.
 * Source of truth extracted from src/config/constants.ts.
 */

import type { VoidReason } from './settlement';

// =============================================================================
// Types
// =============================================================================

export interface BadgeStyle {
  bg: string;
  text: string;
  label: string;
  // Hex colors for React Native (Tailwind classes don't work in RN)
  bgHex?: string;
  textHex?: string;
}

export interface TierConfig {
  color: string;
  icon: string;
  description: string;
}

export interface ConfidenceStyle {
  color: string;
  bg: string;
  border: string;
}

export interface VoidReasonConfig {
  bg: string;
  text: string;
  label: string;
  tooltip: string;
}

// =============================================================================
// Parlay Tiers
// =============================================================================

export const PARLAY_TIERS: Record<string, TierConfig> = {
  'Safe Play': {
    color: 'green',
    icon: 'shield',
    description: 'Conservative picks with strong stats',
  },
  'Value Play': {
    color: 'blue',
    icon: 'gem',
    description: 'Balanced risk/reward',
  },
  'Upside Play': {
    color: 'yellow',
    icon: 'trending-up',
    description: 'Higher odds, multiple edges',
  },
  'Long Shot': {
    color: 'red',
    icon: 'target',
    description: 'High risk, high reward',
  },
  'Lottery Ticket': {
    color: 'purple',
    icon: 'ticket',
    description: 'Very high risk, massive payouts',
  },
  Custom: { color: 'gray', icon: 'pencil', description: 'User-created parlay' },
  'AI Improved': {
    color: 'cyan',
    icon: 'sparkles',
    description: 'AI-optimized lines',
  },
};

// =============================================================================
// Confidence Levels
// =============================================================================

export const CONFIDENCE_LEVELS: Record<string, ConfidenceStyle> = {
  High: {
    color: 'text-theme-success',
    bg: 'bg-theme-success/15',
    border: 'border-theme-success/30',
  },
  Medium: {
    color: 'text-theme-warning',
    bg: 'bg-theme-warning/15',
    border: 'border-theme-warning/30',
  },
  Low: {
    color: 'text-theme-danger',
    bg: 'bg-theme-danger/15',
    border: 'border-theme-danger/30',
  },
};

// =============================================================================
// Origin + Flag Badge Styles
// =============================================================================

export const ORIGIN_BADGES: Record<string, BadgeStyle> = {
  ai: {
    bg: 'bg-blue-500/15',
    text: 'text-blue-400',
    label: 'AI',
    bgHex: 'rgba(59, 130, 246, 0.15)', // blue-500 with 15% opacity
    textHex: '#60a5fa', // blue-400
  },
  manual: {
    bg: 'bg-gray-500/15',
    text: 'text-gray-400',
    label: 'Manual',
    bgHex: 'rgba(139, 146, 168, 0.15)', // silver with 15% opacity
    textHex: '#8B92A8', // silver
  },
  bet: {
    bg: 'bg-cyan-500/15',
    text: 'text-cyan-400',
    label: 'Bet',
    bgHex: 'rgba(6, 182, 212, 0.15)', // cyan-500 with 15% opacity
    textHex: '#06B6D4', // cyan-500
  },
};

export const FLAG_BADGES: Record<string, BadgeStyle> = {
  isTracked: {
    bg: 'bg-theme-success/15',
    text: 'text-theme-success',
    label: 'Tracked',
  },
  isAIImproved: {
    bg: 'bg-cyan-500/15',
    text: 'text-cyan-400',
    label: 'AI Improved',
  },
  isBetVerified: {
    bg: 'bg-theme-success/15',
    text: 'text-theme-success',
    label: 'Verified',
  },
};

// =============================================================================
// Result Badge Styles
// =============================================================================

export const RESULT_BADGES: Record<string, BadgeStyle> = {
  hit: { bg: 'bg-theme-success/20', text: 'text-theme-success', label: 'HIT' },
  miss: { bg: 'bg-theme-danger/20', text: 'text-theme-danger', label: 'MISS' },
  pending: {
    bg: 'bg-theme-warning/15',
    text: 'text-theme-warning',
    label: 'PENDING',
  },
  push: {
    bg: 'bg-theme-surface-hover',
    text: 'text-theme-text-secondary',
    label: 'PUSH',
  },
  dnp: {
    bg: 'bg-theme-warning/15',
    text: 'text-theme-warning',
    label: 'DNP',
  },
  void: {
    bg: 'bg-theme-primary/15',
    text: 'text-theme-primary',
    label: 'VOID',
  },
};

// =============================================================================
// Void Reason Display Config
// =============================================================================

export const VOID_REASON_CONFIG: Record<VoidReason, VoidReasonConfig> = {
  dnp: {
    bg: 'bg-theme-surface-hover',
    text: 'text-theme-text-secondary',
    label: 'DNP',
    tooltip: 'Did Not Play',
  },
  player_not_found: {
    bg: 'bg-orange-900/50',
    text: 'text-orange-300',
    label: 'NOT FOUND',
    tooltip: 'Player not found in system',
  },
  wrong_team: {
    bg: 'bg-purple-900/50',
    text: 'text-purple-300',
    label: 'WRONG TEAM',
    tooltip: 'Player is on a different team',
  },
  game_postponed: {
    bg: 'bg-theme-warning/50',
    text: 'text-yellow-300',
    label: 'POSTPONED',
    tooltip: 'Game was postponed or cancelled',
  },
  market_not_available_for_league: {
    bg: 'bg-red-900/50',
    text: 'text-red-300',
    label: 'INVALID MARKET',
    tooltip: 'Market not available for this league',
  },
  side_unknown: {
    bg: 'bg-theme-surface-hover',
    text: 'text-theme-text-secondary',
    label: 'NO SIDE',
    tooltip: 'Which team was backed could not be determined',
  },
  bet_unrecoverable: {
    bg: 'bg-theme-surface-hover',
    text: 'text-theme-text-secondary',
    label: 'UNKNOWN BET',
    tooltip: 'What was bet could not be determined',
  },
};

// =============================================================================
// Score Badge Helpers (for strategy/prediction recommendation display)
// =============================================================================

export const SCORE_REC_BADGES: Record<string, BadgeStyle> = {
  BET: { bg: 'bg-theme-success/20', text: 'text-theme-success', label: 'BET' },
  'LEAN BET': { bg: 'bg-teal-500/20', text: 'text-teal-400', label: 'LEAN' },
  CAUTION: { bg: 'bg-theme-warning/20', text: 'text-theme-warning', label: 'CAUTION' },
  SKIP: { bg: 'bg-theme-danger/20', text: 'text-theme-danger', label: 'SKIP' },
};
